import React, { useEffect, useState } from "react";

import { CartLines } from "./cart-lines";
import { Button, ButtonStyles } from "@montana/shared/button";
import { Summary } from "./summary";
import { Container } from "@montana/shared/container";
import { Translate } from "@montana/shared/translate";
import { formatString } from "@montana/helpers/string-format/string-format.helper";
import { useCart } from "@montana/containers/cart";
import { ICartPage, IM18USPPromoBar } from '@montana/models/generated/cms.interface';
import { StaticHeroComponent } from "@montana/shared/static-hero/static-hero.component";
import { DynamicBlocks } from "@montana/blocks/dynamic-blocks.component";
import { Accordion } from "@montana/shared/accordion";
import { Voucher } from "./voucher/voucher.component";
import { SvgIcon } from "@montana/shared/svg-icon";
import { usePage } from "@montana/containers/page";
import { useSettings } from "@montana/containers/settings/settings.container";
import { useRouter } from "@montana/containers/router";
import { useDynamicSettings } from "@montana/containers/dynamic-settings";
import { staticSettings } from "@montana/helpers/static-settings.helper";
import { Usp } from "@montana/shared/usp";
import {GuaranteeLink} from "@montana/shared/guarantee-link";

export const CartPage: React.FC<ICartPage> = React.memo((props: ICartPage): JSX.Element => {

    const { bottomBlocks, emptyStateBlocks, topBlocks } = props;
    const { lines, linesToDisplay, updateQuantityLoading, accumulatedQuantity } = useCart();
    const { market } = useSettings();
    const { push } = useRouter();
    const { setPageHasHero } = usePage();
    const { links } = useDynamicSettings();
    const [isVoucherOpen, setIsVoucherOpen] = useState(false);

    const toggleVoucher = (isOpen: boolean) => {
        setIsVoucherOpen(isOpen);
    };

    useEffect(() => {
        setPageHasHero(true);
    }, []);

    useEffect(() => {
        if (market?.disableCart) {
            push('/');
        }
    }, [market]);

    const uspBlock: IM18USPPromoBar | undefined = bottomBlocks?.find(block => block.kind === 'M18USPPromoBar');

    return (
        <Container>
            <section className="cart-page">
                <StaticHeroComponent className="cart__hero">
                    <h1 className="cart__hero-title">
                        <Translate value={(t): string => t.cartPage.productsInBasket} defaultValue="[{0} products in basket]">
                            {(t): string => formatString(t, accumulatedQuantity)}
                        </Translate>
                    </h1>
                </StaticHeroComponent>

                {!!lines?.length && <DynamicBlocks blocks={topBlocks} />}

                <div className="cart dynamic-content-below">
                    <div className="cart__header">
                        <Button className="cart__header-button" style={ButtonStyles.Transparent} href={links?.homePage || "/"}>
                            <Translate value={(t): string => t.cartPage.continueShopping} defaultValue="[continue shopping]" />
                        </Button>
                    </div>

                    {!!linesToDisplay?.length &&
                        <div className="cart__body">
                            <div className="cart__lines">
                                <CartLines />
                            </div>
                            <div className="cart__summary-column">
                                <div className="cart__summary">
                                    <Summary />
                                    <div className="cart__summary-footer">
                                        <Button
                                            href={(staticSettings.b2b ? links?.checkout : links?.checkoutB2C) || '/checkout'}
                                            className="cart__summary-button"
                                            style={ButtonStyles.Primary}
                                            disabled={updateQuantityLoading || !lines?.length}>
                                            <Translate value={(t): string => t.cartPage.goToCheckout} defaultValue="[Proceed to checkout]" />
                                        </Button>
                                        <GuaranteeLink />
                                    </div>
                                    {staticSettings.b2c &&
                                        <div className="cart__voucher">
                                            <Accordion id="voucher" onToggle={toggleVoucher} header={
                                                <div className={`cart__voucher-accordion ${isVoucherOpen ? 'cart__voucher-accordion--open' : ''}`}>
                                                    <Translate value="cartPage.cartPageAddDiscountVoucher" defaultValue="[Add discount voucher]" />
                                                    {isVoucherOpen ? (
                                                        <SvgIcon iconName="chevron_up" />
                                                    ) : (
                                                        <SvgIcon iconName="chevron_down" />
                                                    )}
                                                </div>
                                            }>
                                                <Voucher />
                                            </Accordion>
                                        </div>
                                    }
                                </div>
                                {uspBlock &&
                                    <div className="cart-checkout-usp">
                                        {uspBlock.usPs?.map((usp, index) => (
                                            <Usp key={`usp-block__${uspBlock.epiId}__${index}`} usp={usp} />
                                        ))}
                                    </div>
                                }
                            </div>
                        </div>
                    }
                </div>

                {!!lines?.length ?
                    <DynamicBlocks blocks={bottomBlocks?.filter(block => block.kind !== 'M18USPPromoBar')} />
                    :
                    <DynamicBlocks blocks={emptyStateBlocks} />
                }

            </section>
        </Container >
    );
});
